import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import { Button } from "../../components/button";
import { startBattle } from "../../state/battle";
import { scenarios, secondaries, scoringPacks, getScenario } from "./battle-data";
import { parseOpponentUnits } from "./battle-units";

const STEPS = [
  "Your list",
  "Opponent",
  "Scenario",
  "Secondaries",
  "Scoring",
  "Confirm",
  "First turn",
];

const DeploymentSketch = ({ deployment }) => (
  <div
    className={classNames(
      "battle-setup__map",
      `battle-setup__map--${deployment || "long-edges"}`,
    )}
    aria-hidden
  >
    <span className="battle-setup__map-zone battle-setup__map-zone--a" />
    <span className="battle-setup__map-zone battle-setup__map-zone--b" />
  </div>
);

export const BattleSetup = ({ onDone }) => {
  const dispatch = useDispatch();
  const lists = useSelector((state) => state.lists) || [];
  const [step, setStep] = useState(0);

  const [listId, setListId] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [opponentArmy, setOpponentArmy] = useState("");
  const [opponentListText, setOpponentListText] = useState("");
  const [scenario, setScenario] = useState(scenarios[0]?.id || null);
  const [selectedSecondaries, setSelectedSecondaries] = useState([]);
  const [scoringPack, setScoringPack] = useState(scoringPacks[0]?.id || null);
  const [firstTurn, setFirstTurn] = useState("player");

  const selectableLists = lists.filter((list) => !list.type || list.type !== "folder");
  const selectedList = lists.find(({ id }) => id === listId);
  const selectedScenario = getScenario(scenario);

  const toggleSecondary = (id) => {
    setSelectedSecondaries((current) =>
      current.includes(id)
        ? current.filter((s) => s !== id)
        : [...current, id],
    );
  };

  const begin = () => {
    dispatch(
      startBattle({
        listId: listId || null,
        compositionRule: selectedList?.compositionRule || null,
        opponentName,
        opponentArmy,
        opponentUnits: parseOpponentUnits(opponentListText),
        scenario,
        secondaries: selectedSecondaries,
        scoringPack,
        firstTurn,
      }),
    );
    onDone && onDone();
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="battle-setup">
      <ol className="battle-setup__steps">
        {STEPS.map((name, index) => (
          <li
            key={name}
            className={classNames(
              "battle-setup__step",
              index === step && "battle-setup__step--current",
              index < step && "battle-setup__step--done",
            )}
            onClick={() => index < step && setStep(index)}
          >
            {name}
          </li>
        ))}
      </ol>

      <div className="battle-setup__body">
        {step === 0 && (
          <>
            <h2>Your list</h2>
            <p className="battle-setup__hint">
              Pick a saved list to track casualties per unit, or skip to only
              track totals.
            </p>
            <div className="battle-setup__options">
              <button
                type="button"
                className={classNames(
                  "battle-setup__option",
                  !listId && "battle-setup__option--selected",
                )}
                onClick={() => setListId("")}
              >
                <b>No unit tracking</b>
                <small>Totals only</small>
              </button>
              {selectableLists.map((list) => (
                <button
                  key={list.id}
                  type="button"
                  className={classNames(
                    "battle-setup__option",
                    listId === list.id && "battle-setup__option--selected",
                  )}
                  onClick={() => setListId(list.id)}
                >
                  <b>{list.name}</b>
                  <small>
                    {list.points ? `${list.points} pts` : list.army}
                  </small>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h2>Opponent</h2>
            <label className="battle-setup__label" htmlFor="opponentName">
              Name
            </label>
            <input
              id="opponentName"
              className="input"
              type="text"
              value={opponentName}
              onChange={(e) => setOpponentName(e.target.value)}
              autoComplete="off"
            />
            <label className="battle-setup__label" htmlFor="opponentArmy">
              Army
            </label>
            <input
              id="opponentArmy"
              className="input"
              type="text"
              value={opponentArmy}
              onChange={(e) => setOpponentArmy(e.target.value)}
              autoComplete="off"
            />
            <label className="battle-setup__label" htmlFor="opponentList">
              Their units (optional) — one per line, e.g. “Chosen Knights - 320”
            </label>
            <textarea
              id="opponentList"
              className="input textarea"
              rows="8"
              value={opponentListText}
              onChange={(e) => setOpponentListText(e.target.value)}
            />
          </>
        )}

        {step === 2 && (
          <>
            <h2>Scenario</h2>
            <div className="battle-setup__options">
              {scenarios.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={classNames(
                    "battle-setup__option",
                    scenario === s.id && "battle-setup__option--selected",
                  )}
                  onClick={() => setScenario(s.id)}
                >
                  <b>{s.name}</b>
                  <small>{s.description}</small>
                </button>
              ))}
            </div>
            <Button
              type="secondary"
              spaceTop
              onClick={() =>
                setScenario(
                  scenarios[Math.floor(Math.random() * scenarios.length)].id,
                )
              }
            >
              🎲 Random scenario
            </Button>
            {selectedScenario && (
              <DeploymentSketch deployment={selectedScenario.deployment} />
            )}
          </>
        )}

        {step === 3 && (
          <>
            <h2>Secondary objectives</h2>
            <div className="battle-setup__options">
              {secondaries.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={classNames(
                    "battle-setup__option",
                    selectedSecondaries.includes(s.id) &&
                      "battle-setup__option--selected",
                  )}
                  onClick={() => toggleSecondary(s.id)}
                >
                  <b>
                    {s.name} <small>+{s.points}</small>
                  </b>
                  <small>{s.description}</small>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h2>Scoring pack</h2>
            <div className="battle-setup__options">
              {scoringPacks.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={classNames(
                    "battle-setup__option",
                    scoringPack === p.id && "battle-setup__option--selected",
                  )}
                  onClick={() => setScoringPack(p.id)}
                >
                  <b>
                    {p.name}
                    {p.verified === false && <small> (check brackets)</small>}
                  </b>
                  <small>
                    {p.brackets.length
                      ? `${p.brackets.length} brackets`
                      : "No battle points"}
                  </small>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 5 && (
          <>
            <h2>Summary</h2>
            <dl className="battle-setup__summary">
              <dt>Your list</dt>
              <dd>{selectedList?.name || "No unit tracking"}</dd>
              <dt>Opponent</dt>
              <dd>
                {opponentName || "—"}
                {opponentArmy ? ` (${opponentArmy})` : ""}
              </dd>
              <dt>Scenario</dt>
              <dd>{selectedScenario?.name || "—"}</dd>
              <dt>Secondaries</dt>
              <dd>
                {selectedSecondaries.length
                  ? selectedSecondaries
                      .map(
                        (id) => secondaries.find((s) => s.id === id)?.name,
                      )
                      .join(", ")
                  : "None"}
              </dd>
              <dt>Scoring</dt>
              <dd>
                {scoringPacks.find((p) => p.id === scoringPack)?.name || "—"}
              </dd>
            </dl>
          </>
        )}

        {step === 6 && (
          <>
            <h2>Who goes first?</h2>
            <div className="battle-setup__options battle-setup__options--row">
              <button
                type="button"
                className={classNames(
                  "battle-setup__option",
                  firstTurn === "player" && "battle-setup__option--selected",
                )}
                onClick={() => setFirstTurn("player")}
              >
                <b>You</b>
              </button>
              <button
                type="button"
                className={classNames(
                  "battle-setup__option",
                  firstTurn === "opponent" && "battle-setup__option--selected",
                )}
                onClick={() => setFirstTurn("opponent")}
              >
                <b>Opponent</b>
              </button>
            </div>
          </>
        )}
      </div>

      <div className="battle-setup__nav">
        {step > 0 && (
          <Button type="secondary" onClick={back}>
            Back
          </Button>
        )}
        {step < STEPS.length - 1 && (
          <Button type="primary" onClick={next}>
            Next
          </Button>
        )}
        {step === STEPS.length - 1 && (
          <Button type="primary" onClick={begin}>
            Start battle
          </Button>
        )}
      </div>
    </div>
  );
};
